import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Event } from '@domain/events/models/Event';
import { CreateEventRequestDTO, UpdateEventRequestDTO } from '@infrastructure/events/dto/EventDTO';

export const useEvents = () => {
  const queryClient = useQueryClient();

  const {
    data: events,
    isLoading: isLoadingEvents,
    error: eventsError
  } = useQuery<Event[]>({
    queryKey: ['events'],
    queryFn: async () => {
      const result = await container.eventRepository.getEvents();
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const createEventMutation = useMutation({
    mutationFn: async (event: CreateEventRequestDTO) => {
      const result = await container.eventRepository.createEvent(event);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const updateEventMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateEventRequestDTO }) => {
      const result = await container.eventRepository.updateEvent(id, updates);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      queryClient.invalidateQueries({ queryKey: ['product-events'] });
    }
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await container.eventRepository.deleteEvent(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['product-events'] });
    }
  });

  const linkProductMutation = useMutation({
    mutationFn: async ({ eventId, productIds }: { eventId: string; productIds: string[] }) => {
      const result = await container.eventRepository.linkProduct(eventId, productIds);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-products'] });
      queryClient.invalidateQueries({ queryKey: ['product-events'] });
    }
  });

  const unlinkProductMutation = useMutation({
    mutationFn: async ({ eventId, productIds }: { eventId: string; productIds: string[] }) => {
      const result = await container.eventRepository.unlinkProduct(eventId, productIds);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-products'] });
      queryClient.invalidateQueries({ queryKey: ['product-events'] });
    }
  });

  return {
    events,
    isLoading: isLoadingEvents,
    error: eventsError,
    createEvent: createEventMutation.mutateAsync,
    updateEvent: updateEventMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutateAsync,
    isCreating: createEventMutation.isPending,
    isUpdating: updateEventMutation.isPending,
    isDeleting: deleteEventMutation.isPending,
    linkProduct: linkProductMutation.mutateAsync,
    unlinkProduct: unlinkProductMutation.mutateAsync,
    isLinking: linkProductMutation.isPending,
    isUnlinking: unlinkProductMutation.isPending,
  };
};

export const useEvent = (eventId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: event,
    isLoading: isLoadingEvent,
    error: eventError
  } = useQuery<Event>({
    queryKey: ['event', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('Event ID is required');
      const result = await container.eventRepository.getEvent(eventId);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!eventId,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const {
    data: eventProducts,
    isLoading: isLoadingEventProducts
  } = useQuery<string[]>({
    queryKey: ['event-products', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('Event ID is required');
      const result = await container.eventRepository.getEventProducts(eventId);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!eventId,
    refetchOnWindowFocus: false,
  });

  const linkProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      if (!eventId) throw new Error('Event ID is required');
      const result = await container.eventRepository.linkProduct(eventId, [productId]);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },  
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-products', eventId] });
      queryClient.invalidateQueries({ queryKey: ['product-events'] });
    }
  });

  const unlinkProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      if (!eventId) throw new Error('Event ID is required');
      const result = await container.eventRepository.unlinkProduct(eventId, [productId]);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-products', eventId] });
      queryClient.invalidateQueries({ queryKey: ['product-events'] });
    }
  });

  return {
    event,
    eventProducts,
    isLoading: isLoadingEvent || isLoadingEventProducts,
    error: eventError,
    linkProduct: linkProductMutation.mutateAsync,
    unlinkProduct: unlinkProductMutation.mutateAsync,
    isLinking: linkProductMutation.isPending,
    isUnlinking: unlinkProductMutation.isPending,
  };
};

